//Imports
import React, { useCallback, useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Actions
import { manageCreateEditManageInput, reset } from "./redux/slice";

//Controls
import { Button, Form, Input, Modal } from "@gpstrackit/gtc-react-controls";

//Components
import {
  NotificationManager,
  NotificationContainer,
} from "Components/Notifications";

import { hasEmojiValidation } from "Modules/manage/.globals/utils";
import { getRequiredTrimmedNonEmptyRules } from "Modules/.common/utils/gtcFormItemRules";

const CreateEditManageInput = ({ refresh }) => {
  //States
  const [formData, setFormData] = useState({
    title: null,
    onLabel: null,
    offLabel: null,
  });

  //Language
  const { messages } = useIntl();

  //Form refs
  const createEditInputFormRef = Form.useForm();

  //Redux
  const dispatch = useDispatch();
  const { input, loading, error, updateManageInputResult, open } = useSelector(
    (state) => state.manageCreateEditManageInputRedux
  );

  //Memos
  const title = useMemo(
    () =>
      input?.id
        ? `${messages["manage_createEditInput_EditTitle"]}: "${input?.title}"`
        : messages["manage_createEditInput_CreateTitle"],
    [input]
  );

  const textFieldRules = useMemo(
    () => getRequiredTrimmedNonEmptyRules(messages["required_field"]),
    [messages]
  );

  //Handlers and utils
  const handleUpdateManageInputResult = useCallback(() => {
    const title = String(formData.title ?? "").trim();
    const onLabel = String(formData.onLabel ?? "").trim();
    const offLabel = String(formData.offLabel ?? "").trim();

    if (hasEmojiValidation(title))
      return NotificationManager.warning(
        messages["manage_createEditInput_warning_characters"],
        messages["warning"],
        3000,
        null,
        null,
        "warning"
      );
    if (hasEmojiValidation(onLabel))
      return NotificationManager.warning(
        messages["manage_createEditInput_warning_characters_onLabel"],
        messages["warning"],
        3000,
        null,
        null,
        "warning"
      );
    if (hasEmojiValidation(offLabel))
      return NotificationManager.warning(
        messages["manage_createEditInput_warning_characters_offLabel"],
        messages["warning"],
        3000,
        null,
        null,
        "warning"
      );
    const payload = {
      id: input?.id,
      title,
      onLabel,
      offLabel,
    };
    dispatch(manageCreateEditManageInput(payload));
  }, [formData, input]);

  const handleClose = useCallback(() => {
    dispatch(reset());
  }, []);

  const handleSuccess = useCallback(() => {
    NotificationManager.success(
      input?.id
        ? messages["manage_createEditInput_success_edit"]
        : messages["manage_createEditInput_success"],
      messages["success"],
      3000,
      null,
      null,
      "success"
    );
    refresh();
    handleClose();
  }, [input]);

  useEffect(() => {
    if (!updateManageInputResult?.id) return;
    handleSuccess();
  }, [updateManageInputResult]);

  useEffect(() => {
    if (!error) return;
    let errorMessage = error?.error || messages["errorMessage"];
    if (errorMessage?.includes && errorMessage.includes("CompanyReadonly")) {
      errorMessage = messages["errorCompanyReadOnly"];
    } else if (
      error?.errorData?.errors?.find((err) => err?.errorInfo?.code === 1010)
    ) {
      errorMessage =
        messages["manage_createEditInput_duplicatedInputErrorMessage"];
    }
    NotificationManager.error(
      errorMessage,
      messages["error"],
      4000,
      null,
      null,
      "error"
    );
    dispatch(reset({ error: null }));
  }, [error]);

  useEffect(() => {
    if (!open || !input?.id) return;
    setTimeout(() => {
      const { title, onLabel, offLabel } = input;
      const defaultData = {
        title,
        onLabel,
        offLabel,
      };
      setFormData(defaultData);
      createEditInputFormRef.setValues(defaultData);
    });
  }, [input, open]);

  useEffect(() => {
    return () => reset();
  }, []);

  return (
    <Modal
      title={
        title && title.length > 48 ? title.substring(0, 45) + '..."' : title
      }
      open={open}
      handleClose={handleClose}
      position="right"
      style={{ zIndex: "auto" }}
      size="small"
      loading={loading}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            isDisabled={loading}
            style={{ marginRight: 8 }}
            onClick={handleClose}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>

          <Button
            isDisabled={loading}
            onClick={() => {
              createEditInputFormRef?.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        <Form
          ref={createEditInputFormRef.ref}
          loading={loading}
          onValidSubmit={() => handleUpdateManageInputResult()}
          onChangeValues={() => {}}
        >
          <Form.Item
            name="title"
            label={messages["manage_createEditInput_TitleInputLabel"]}
            rules={textFieldRules}
          >
            <Input
              id="title"
              type="text"
              placeholder={messages["manage_createEditInput_TitleInputLabel"]}
              onChange={(value) => setFormData({ ...formData, title: value })}
              maxLength={20}
            />
          </Form.Item>
          <Form.Item
            name="onLabel"
            label={messages["manage_createEditInput_onLabelInputLabel"]}
            rules={textFieldRules}
          >
            <Input
              id="onLabel"
              type="text"
              placeholder={messages["manage_createEditInput_onLabelInputLabel"]}
              onChange={(value) => setFormData({ ...formData, onLabel: value })}
              maxLength={20}
            />
          </Form.Item>
          <Form.Item
            name="offLabel"
            label={messages["manage_createEditInput_offLabelInputLabel"]}
            rules={textFieldRules}
          >
            <Input
              id="offLabel"
              type="text"
              placeholder={
                messages["manage_createEditInput_offLabelInputLabel"]
              }
              onChange={(value) =>
                setFormData({ ...formData, offLabel: value })
              }
              maxLength={20}
            />
          </Form.Item>
        </Form>
      </div>
      <NotificationContainer />
    </Modal>
  );
};

export default CreateEditManageInput;
